import { useState } from 'react';
import { Pencil, Trash2, Webhook } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { CredentialTestButton } from './CredentialTestButton';
import { DeleteCredentialDialog } from './DeleteCredentialDialog';
import { useCredentialsList, useRegisterWebhook } from '@/hooks/useCredentials';
import type { Credential } from '@/types/api.types';
import { Key } from 'lucide-react';

interface Props {
  onEdit: (credential: Credential) => void;
}

function CredentialListSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-8 w-24" />
        </div>
      ))}
    </div>
  );
}

export function CredentialList({ onEdit }: Props) {
  const { data: credentials = [], isLoading } = useCredentialsList();
  const { mutate: registerWebhook, isPending: isRegistering } = useRegisterWebhook();
  const [deleteTarget, setDeleteTarget] = useState<Credential | null>(null);

  if (isLoading) return <CredentialListSkeleton />;

  if (credentials.length === 0) {
    return (
      <EmptyState
        icon={Key}
        title="Nenhuma credencial cadastrada"
        description="Adicione uma credencial Zenvia para começar a disparar mensagens."
      />
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label</TableHead>
            <TableHead>Sender ID</TableHead>
            <TableHead>Token</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {credentials.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">{c.label}</TableCell>
              <TableCell>{c.senderId}</TableCell>
              <TableCell>
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {c.apiTokenMasked}
                </code>
              </TableCell>
              <TableCell>
                <Badge variant={c.isActive ? 'default' : 'secondary'}>
                  {c.isActive ? 'Ativa' : 'Inativa'}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-1">
                  <CredentialTestButton credentialId={c.id} />
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Registrar webhook na Zenvia"
                    disabled={isRegistering}
                    onClick={() => registerWebhook(c.id)}
                  >
                    <Webhook className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onEdit(c)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget(c)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {deleteTarget && (
        <DeleteCredentialDialog
          credentialId={deleteTarget.id}
          label={deleteTarget.label}
          open={!!deleteTarget}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        />
      )}
    </>
  );
}
